import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Download, FileText, Lock } from "lucide-react";
import { useT } from "@/i18n";
import { RequestStatusBadge } from "@/components/RequestStatusBadge";
import { Badge, Button, Card, EmptyState, Skeleton } from "@/components/ui";
import { certificateRequestsApi, type CustomerArtifact } from "@/lib/api";
import { requestTypeLabel, validityLabel } from "@/lib/certificate-requests";
import { saveBlob } from "@/lib/download";
import { formatDate } from "@/lib/format";
import { useState } from "react";

export function CertificateRequestDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, locale } = useT();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ["me", "certificate-requests"],
    queryFn: certificateRequestsApi.list,
  });

  const request = q.data?.requests.find((item) => item.id === id);

  async function downloadArtifact(artifact: CustomerArtifact) {
    setDownloadingId(artifact.id);
    try {
      const { blob, fileName } = await certificateRequestsApi.downloadArtifact(artifact.id);
      saveBlob(blob, fileName);
      toast.success(fileName);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("onb.err_unknown"));
    } finally {
      setDownloadingId(null);
    }
  }

  if (q.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-56" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  if (q.isError) {
    return (
      <Card className="p-6 text-sm text-red-700">
        {q.error instanceof Error ? q.error.message : t("onb.err_unknown")}
      </Card>
    );
  }

  if (!request) {
    return (
      <div className="py-20 text-center">
        <h2 className="text-xl font-semibold">{t("cert.request_not_found")}</h2>
        <Link to="/certificados" className="mt-6 btn-secondary inline-flex">
          {t("cert.back_to_list")}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("cert.back_to_list")}
      </button>

      <Card className="p-6 sm:p-8">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <RequestStatusBadge status={request.status} />
              <Badge tone="neutral">{t("cert.issuer")}</Badge>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                {t("cert.request_detail_title")}
              </p>
              <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
                {requestTypeLabel(t, request.type)}
              </h1>
            </div>
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:min-w-80">
            <div>
              <dt className="text-xs uppercase tracking-wider text-slate-500">{t("cert.class")}</dt>
              <dd className="font-medium text-slate-900">{request.class}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-slate-500">{t("cert.validity")}</dt>
              <dd className="font-medium text-slate-900">
                {validityLabel(t, request.validityMonths)}
                {(request.validFrom || request.validUntil) && (
                  <span className="block text-xs font-normal text-slate-500">
                    {request.validFrom ?? "—"} → {request.validUntil ?? "—"}
                  </span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-slate-500">{t("cert.request_created_on")}</dt>
              <dd className="font-medium text-slate-900">{formatDate(request.createdAt, locale)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-slate-500">{t("cert.request_updated_on")}</dt>
              <dd className="font-medium text-slate-900">{formatDate(request.updatedAt, locale)}</dd>
            </div>
          </dl>
        </div>

        <dl className="mt-6 grid gap-3 border-t border-slate-100 pt-5 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs uppercase tracking-wider text-slate-500">{t("cert.serial")}</dt>
            <dd className="mt-1 font-mono text-xs text-slate-900">
              {request.certificateSerial ?? <span className="text-slate-400">—</span>}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wider text-slate-500">{t("cert.request_status")}</dt>
            <dd className="mt-1 text-slate-700">{statusText(t, request.status)}</dd>
          </div>
        </dl>
      </Card>

      <Card className="p-6">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("cert.request_artifacts")}
        </h2>
        {request.artifacts.length === 0 ? (
          <EmptyState
            icon={<FileText className="h-6 w-6" />}
            title={t("cert.no_public_artifacts")}
          />
        ) : (
          <ul className="mt-5 divide-y divide-slate-100">
            {request.artifacts.map((artifact) => (
              <li key={artifact.id} className="flex items-center gap-3 py-3">
                <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-slate-100 text-slate-700">
                  <FileText className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-900">
                    {artifact.fileName ?? artifactLabel(t, artifact.type)}
                  </p>
                  <p className="text-xs text-slate-500">
                    {artifactLabel(t, artifact.type)} · {formatDate(artifact.createdAt, locale)}
                  </p>
                </div>
                {artifact.encrypted && (
                  <Badge tone="neutral">
                    <Lock className="h-3 w-3" />
                  </Badge>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => downloadArtifact(artifact)}
                  loading={downloadingId === artifact.id}
                >
                  <Download className="h-4 w-4" />
                  {t("common.download")}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function statusText(t: ReturnType<typeof useT>["t"], status: string) {
  const keys: Record<string, Parameters<typeof t>[0]> = {
    pending: "status.pending",
    verified: "status.verified",
    issued: "status.issued",
    rejected: "status.rejected",
    revoked: "status.revoked",
    expired: "status.expired",
  };
  return keys[status] ? t(keys[status]) : status;
}

function artifactLabel(t: ReturnType<typeof useT>["t"], type: string) {
  const keys: Record<string, Parameters<typeof t>[0]> = {
    rut: "artifact.rut",
    camara_comercio: "artifact.camara_comercio",
    cedula: "artifact.cedula",
    poder: "artifact.poder",
    formulario: "artifact.formulario",
    attachment: "artifact.attachment",
    response_document: "artifact.response_document",
  };
  return keys[type] ? t(keys[type]) : type.replace(/_/g, " ");
}
